import { useState } from 'react';
import printJS from 'print-js';
import { toast } from 'react-toastify';

interface PrintOptions {
    title?: string;
    fileName?: string;
    landscape?: boolean;
    showHeader?: boolean;
    showFooter?: boolean;
    customStyles?: string;
}

const usePrint = () => {
    const [isPrinting, setIsPrinting] = useState(false);
    const defaultPrintStyles = `
        @media print {
            .no-print {
                display:none !important;
            }
            
            .print-only {
                display : block !important;
            }
            
            body {
                font-family : 'Arial', sans-serif;
                font-size : 12px;
                line-height:1.4;
                color:#000;
            }
            
            @page{
                size:A4 portrait;
                margin:15mm
            }
            
            .print-header{
                text-align:center;
                margin-bottom:20px;
                padding-bottom:10px;
                border-bottom: 2px solid #333;
            }
            .print-header h1 {
                font-size: 24px;
                margin: 0;
                color: #1976d2;
            }
            .print-footer {
                margin-top: 30px;
                padding-top: 10px;
                border-top: 1px solid #ddd;
                text-align: center;
                font-size: 10px;
                color: #666;
            }
            
        }
        .print-only {
            display: none;
        }
    `;

    const handlePrint = async (elementId: string, options: PrintOptions = {}) => {
        const { title = 'Report', fileName, landscape = false, showHeader = true, showFooter = true, customStyles = '' } = options;
        try {
            const printableElement = document.getElementById(elementId);
            if (!printableElement) {
                throw new Error('Printable element not found');
            }

            addPrintClasses(printableElement, showHeader, showFooter);
            const printHTML = printableElement.innerHTML;

            printJS({
                printable: printHTML,
                type: 'raw-html',
                documentTitle: fileName || `${title}_${new Date().toISOString().split('T')[0]}`,
                style: `
                    @media print {
                        @page {
                            size: A4 ${landscape ? 'landscape' : 'portrait'};
                            margin: 15mm;
                        }
                    }
                    ${defaultPrintStyles}
                    ${customStyles}
                `,
                onPrintDialogClose: () => {
                    removePrintClasses(printableElement);
                    setIsPrinting(false);
                },
                onError: (error) => {
                    toast.error("ailed to Print");
                    removePrintClasses(printableElement);
                    setIsPrinting(false);
                }
            })
        } catch (error) {
            console.error("Print error:", error);
            toast.error("Failed to print");
            setIsPrinting(false)
        }
    }

    const addPrintClasses = (element: HTMLElement, showHeader: boolean, showFooter: boolean) => {
        // Add print header if needed
        if (showHeader) {
            const header = element.querySelector('.print-header');
            if (!header) {
                const headerDiv = document.createElement('div');
                headerDiv.className = 'print-header print-only';
                headerDiv.innerHTML = `
          <h1>${document.title || 'Report'}</h1>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
        `;
                element.prepend(headerDiv);
            }
        }

        // Add print footer if needed
        if (showFooter) {
            const footer = element.querySelector('.print-footer');
            if (!footer) {
                const footerDiv = document.createElement('div');
                footerDiv.className = 'print-footer print-only';
                footerDiv.innerHTML = `
          <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          <p>Page 1 of 1</p>
        `;
                element.appendChild(footerDiv);
            }
        }
    };

    const removePrintClasses = (element: HTMLElement) => {
        const printOnlyElements = element.querySelectorAll('.print-only');
        printOnlyElements.forEach(el => el.remove());
    };

    return {
        isPrinting,
        handlePrint,
        defaultPrintStyles
    };
}

export default usePrint;